/**
 * Les compteurs de la barre latérale — **des données, jamais des constantes**.
 *
 * La maquette montre des pastilles chiffrées : 18 dossiers d'identité, 7
 * entreprises, 9 alertes, 2 tickets, 4 guichets, 3 écarts financiers. Ces
 * nombres sont ceux d'une maquette. Aucune route ne les sert aujourd'hui, et
 * les recopier dans le code afficherait en production un compteur faux et
 * immobile — un mensonge à l'écran, découvert par le premier analyste qui
 * traiterait les dix-huit dossiers sans voir la pastille bouger.
 *
 * D'où la règle tenue ici : **la pastille ne se rend que si un compte est
 * fourni**. Tant que rien n'en fournit, la barre latérale n'en montre aucune.
 * Le jour où une route les sert, il suffira d'envelopper l'application dans
 * `ModuleBadgeCountsProvider` — le rendu, lui, est déjà écrit et éprouvé.
 *
 * Un contexte plutôt qu'une propriété : la coquille est montée par le routeur,
 * qui ne passe rien, et faire descendre les comptes de main en main jusqu'à la
 * barre latérale demanderait de traverser des composants qui n'en ont que
 * faire.
 */

import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

/**
 * Les comptes à afficher, par identifiant de module.
 *
 * Un module **absent** de la table n'a pas de compteur — ce n'est pas la même
 * chose qu'un compteur à zéro, qui se rendrait, lui, comme un « 0 ».
 */
export type ModuleBadgeCounts = ReadonlyMap<string, number>;

/** Aucun compte : l'état tant qu'aucune route n'en sert. */
export const noModuleBadgeCounts: ModuleBadgeCounts = new Map<string, number>();

const ModuleBadgeCountsContext = createContext<ModuleBadgeCounts>(noModuleBadgeCounts);

/** Rend [counts] disponible à la barre latérale. */
export function ModuleBadgeCountsProvider({
  counts,
  children,
}: {
  readonly counts: ModuleBadgeCounts;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <ModuleBadgeCountsContext.Provider value={counts}>{children}</ModuleBadgeCountsContext.Provider>
  );
}

/** Les comptes du contexte. Aucun, hors du fournisseur. */
export function useModuleBadgeCounts(): ModuleBadgeCounts {
  return useContext(ModuleBadgeCountsContext);
}
